/**
 * Settings sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchSettings,
  updateSettings,
  setSettings,
  setLoading,
  setError,
} from './settingsSlice';
import { presentLoader, dismissLoader } from './commonSlice';

/**
 * Fetch settings saga
 */
function* fetchSettingsSaga(): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.settings.get);

    if (response?.success) {
      yield put(setSettings(response.data || response));
    } else {
      // Settings endpoint doesn't exist yet - use default settings
      yield put(setSettings(null));
    }
  } catch (error: any) {
    // Handle 404 gracefully - settings endpoint doesn't exist yet
    if (error?.status === 404 || error?.response?.status === 404) {
      // Settings endpoint not implemented yet - use default settings
      yield put(setSettings(null));
    } else {
      const message = error?.message || 'Failed to fetch settings';
      yield put(setError(message));
    }
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update settings saga
 */
function* updateSettingsSaga(action: ReturnType<typeof updateSettings>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.put, endPoints.settings.update, action.payload);

    if (response?.success) {
      yield put(setSettings(response.data || action.payload));
      toast.success('Settings updated successfully');
    } else {
      toast.error('Failed to update settings');
      yield put(setError('Failed to update settings'));
    }
  } catch (error: any) {
    // Handle 404 gracefully - settings endpoint doesn't exist yet
    if (error?.status === 404 || error?.response?.status === 404) {
      // Settings endpoint not implemented yet - save to localStorage as fallback
      try {
        localStorage.setItem('appSettings', JSON.stringify(action.payload));
        yield put(setSettings(action.payload));
        toast.success('Settings saved locally (backend endpoint not available)');
      } catch (localError) {
        toast.error('Failed to save settings');
        yield put(setError('Failed to save settings'));
      }
    } else {
      const message = error?.message || 'Failed to update settings';
      toast.error(message);
      yield put(setError(message));
    }
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

export function* settingsSagas(): Generator<any, void, any> {
  yield takeLatest(fetchSettings, fetchSettingsSaga);
  yield takeLatest(updateSettings, updateSettingsSaga);
}

